import React, { useState, useCallback, useMemo } from 'react';
import { IconsType } from 'components/Icon';
import Dialog from 'components/Dialog';
import useStyles from './fundedInvestingManualKycDialog.styles';
import { useTranslation } from 'react-i18next';
import { FundedInvestingManualKycDialogProps } from './fundedInvestingManualKycDialog.scheme';
import { UploadedFileWithType } from '../FundedSelect';
import VerifyIdentityStep from './FundedInvestingComponents/verifyIdentityStep.render';
import FundedStep from './FundedInvestingComponents/fundedStep.render';
import TermsStep from './FundedInvestingComponents/termsStep.render';
import VerificationInProgressStep from './FundedInvestingComponents/verificationInProgressStep.render';
import YouAreVerifiedStep from './FundedInvestingComponents/youAreVerifiedStep.render';
import DocumentResubmissionStep from './FundedInvestingComponents/documentResubmissionStep.render';
import VerificationAssistanceRequiredStep from './FundedInvestingComponents/verificationAssistanceRequiredStep.render';
import {
    AccountInfoParsedKycVerificationStatusEnum,
    ManualKycRequestKycStatusEnum,
    UploadFileWithDocumentTypeRequest,
    UploadFileWithDocumentTypeRequestDocumentTypeEnum,
} from 'services/generatedClientFromSwagger';
import config from 'config/common';
import IconBox from 'components/IconBox/iconBox.render';
import { IconColor } from 'components/IconBox/iconBox.scheme';
import SumSubDialogStep from './FundedInvestingComponents/sumSubDialogStep.render';

const autoKycSumSub = config.featuresFlags['autoKycSumSub'];
const KYC_INCOMPLETE_STATUSES = new Set([
    AccountInfoParsedKycVerificationStatusEnum.NotStarted,
    AccountInfoParsedKycVerificationStatusEnum.DocumentUploaded,
    AccountInfoParsedKycVerificationStatusEnum.InProgress,
    AccountInfoParsedKycVerificationStatusEnum.Rejected,
    AccountInfoParsedKycVerificationStatusEnum.Resubmission,
]);

const FundedInvestingManualKycDialog: React.FunctionComponent<FundedInvestingManualKycDialogProps> = ({
    isOpen,
    onClose,
    onAccept,
    userInfo,
    challengeId,
    isFundedChallenge,
    requireKyc,
    isFirstAuditionChallenge,
    updateStatusManualKyc,
    manualKycLoading,
}) => {
    const [isTermsCheckboxChecked, setIsTermsCheckboxChecked] = useState(false);
    const [selectedFileType, setSelectedFileType] = useState<
        UploadFileWithDocumentTypeRequestDocumentTypeEnum | undefined
    >(UploadFileWithDocumentTypeRequestDocumentTypeEnum.NationalIdCard);
    const [files, setFiles] = useState<UploadedFileWithType[]>([]);
    const { t } = useTranslation('prop');
    const classes = useStyles();

    const handleTermsCheckboxChange = useCallback(() => {
        setIsTermsCheckboxChecked((prevState) => !prevState);
    }, []);

    const currentStep = useMemo(
        () => (config.featuresFlags['manualKyc'] ? userInfo.kycVerificationStatus : undefined),
        [userInfo.kycVerificationStatus]
    );

    const onSumSubSubmission = useCallback(() => {
        updateStatusManualKyc({
            challengeId,
            kycStatus: ManualKycRequestKycStatusEnum.InProgress,
        });
    }, [challengeId, updateStatusManualKyc]);

    const handleNextStep = useCallback(
        (optionalStatus?: AccountInfoParsedKycVerificationStatusEnum) => {
            const status = optionalStatus || currentStep;
            if (!status) return;

            switch (status) {
                case AccountInfoParsedKycVerificationStatusEnum.NotStarted:
                    updateStatusManualKyc({ kycStatus: ManualKycRequestKycStatusEnum.NotStarted });
                    break;
                case AccountInfoParsedKycVerificationStatusEnum.Approved:
                    if (isFundedChallenge) {
                        updateStatusManualKyc({
                            challengeId,
                            kycStatus: ManualKycRequestKycStatusEnum.TraderAgreementAccepted,
                        });
                        onAccept();
                    } else {
                        updateStatusManualKyc({
                            challengeId,
                            kycStatus: ManualKycRequestKycStatusEnum.Approved,
                        });
                        onClose();
                    }
                    break;
                case AccountInfoParsedKycVerificationStatusEnum.Resubmission:
                case AccountInfoParsedKycVerificationStatusEnum.DocumentUploaded:
                    const uploadedFiles: UploadFileWithDocumentTypeRequest[] = files.map(({ name, base64 }) => ({
                        fileName: name,
                        fileContent: base64?.split(',')[1],
                        documentType: selectedFileType,
                        accountId: userInfo.id,
                    }));
                    updateStatusManualKyc({
                        kycStatus: ManualKycRequestKycStatusEnum.DocumentUploaded,
                        files: uploadedFiles,
                    });
                    break;
                case AccountInfoParsedKycVerificationStatusEnum.InProgress:
                case AccountInfoParsedKycVerificationStatusEnum.Rejected:
                    onAccept();
                    onClose();
                    break;
                default:
                    break;
            }
        },
        [
            challengeId,
            currentStep,
            files,
            isFundedChallenge,
            onAccept,
            onClose,
            selectedFileType,
            updateStatusManualKyc,
            userInfo.id,
        ]
    );

    const renderIconBox = useCallback(
        (color: IconColor, iconType: IconsType, className?: string) => (
            <div className={classes.iconsContainer}>
                <IconBox variant="border" color={color} size={48} iconType={iconType} className={className} />
            </div>
        ),
        [classes.iconsContainer]
    );
    const shouldUseSumSubStyles =
        autoKycSumSub && currentStep === AccountInfoParsedKycVerificationStatusEnum.NotStarted;

    const renderTermsStep = useCallback(
        () => (
            <>
                {renderIconBox('success', IconsType.shield_plus)}
                <TermsStep
                    isCheckboxChecked={isTermsCheckboxChecked}
                    handleCheckboxChange={handleTermsCheckboxChange}
                    onSubmit={onAccept}
                />
            </>
        ),
        [renderIconBox, isTermsCheckboxChecked, handleTermsCheckboxChange, onAccept]
    );

    const renderStep = useCallback(() => {
        const shouldShowTermsWithoutKyc =
            !requireKyc && isFundedChallenge && currentStep && KYC_INCOMPLETE_STATUSES.has(currentStep);

        if (shouldShowTermsWithoutKyc) {
            return renderTermsStep();
        }

        switch (currentStep) {
            case AccountInfoParsedKycVerificationStatusEnum.NotStarted:
                return !autoKycSumSub ? (
                    <>
                        {renderIconBox('success', IconsType.coinsStacked2)}
                        <VerifyIdentityStep
                            files={files}
                            setFiles={setFiles}
                            t={t}
                            uploadD_processLoading={false}
                            handleNextStep={handleNextStep}
                            disabled={manualKycLoading}
                        />
                    </>
                ) : (
                    <SumSubDialogStep onSumSubSubmission={onSumSubSubmission} />
                );
            case AccountInfoParsedKycVerificationStatusEnum.DocumentUploaded:
            case AccountInfoParsedKycVerificationStatusEnum.InProgress:
                return (
                    <>
                        {renderIconBox('success', IconsType.shield_plus)}
                        <VerificationInProgressStep onClose={onClose} />
                    </>
                );
            case AccountInfoParsedKycVerificationStatusEnum.Approved:
                return (
                    <>
                        {renderIconBox('success', IconsType.shield_tick)}
                        <YouAreVerifiedStep
                            handleCheckboxChange={handleTermsCheckboxChange}
                            isCheckboxChecked={isFundedChallenge ? isTermsCheckboxChecked : true}
                            handleNextStep={handleNextStep}
                            disabled={manualKycLoading}
                            showAgreementCheckbox={isFundedChallenge}
                        />
                    </>
                );
            case AccountInfoParsedKycVerificationStatusEnum.Resubmission:
                return (
                    <DocumentResubmissionStep
                        renderChildren={() => (
                            <>
                                {!autoKycSumSub ? (
                                    <>
                                        {renderIconBox('success', IconsType.coinsStacked2)}
                                        <VerifyIdentityStep
                                            files={files}
                                            setFiles={setFiles}
                                            t={t}
                                            uploadD_processLoading={false}
                                            handleNextStep={handleNextStep}
                                            disabled={manualKycLoading}
                                        />
                                    </>
                                ) : (
                                    <SumSubDialogStep onSumSubSubmission={onSumSubSubmission} />
                                )}
                            </>
                        )}
                        handleNextStep={handleNextStep}
                    />
                );
            case AccountInfoParsedKycVerificationStatusEnum.Rejected:
                return (
                    <>
                        {renderIconBox('gray', IconsType.shield_plus, classes.iconColor)}
                        <VerificationAssistanceRequiredStep handleNextStep={onClose} />
                    </>
                );
            default:
                // Only show FundedStep (starts KYC) if KYC is required for this challenge
                if (config.featuresFlags['manualKyc'] && !userInfo.kycVerificationStatus && requireKyc) {
                    return (
                        <>
                            {renderIconBox('success', IconsType.coinsStacked2)}
                            <FundedStep
                                isAudition={!isFundedChallenge}
                                isFirstAudition={!isFundedChallenge && isFirstAuditionChallenge}
                                handleNextStep={() =>
                                    handleNextStep(AccountInfoParsedKycVerificationStatusEnum.NotStarted)
                                }
                                loading={manualKycLoading}
                            />
                        </>
                    );
                }
                if (isFundedChallenge) {
                    return renderTermsStep();
                }
                return null;
        }
    }, [
        renderTermsStep,
        isFundedChallenge,
        requireKyc,
        isFirstAuditionChallenge,
        currentStep,
        renderIconBox,
        files,
        t,
        handleNextStep,
        manualKycLoading,
        onClose,
        handleTermsCheckboxChange,
        isTermsCheckboxChecked,
        userInfo.kycVerificationStatus,
        onSumSubSubmission,
    ]);

    return (
        <Dialog
            eventLogName="Funded investing manual kyc"
            open={isOpen}
            onClose={onClose}
            classes={
                !shouldUseSumSubStyles
                    ? { paper: classes.dialogPaper, root: classes.backDrop }
                    : { paper: classes.dialogPaperSumSub, root: classes.backDrop }
            }
            closeButtonIcon={{ icon: IconsType.x_close }}
        >
            {renderStep()}
        </Dialog>
    );
};

export default FundedInvestingManualKycDialog;
